/**
 * Chart settings: how each trading line looks (entry, TP, SL, pending orders …), the chart type,
 * candle colours, scale and display options. Saved per user so they follow the trader across devices.
 * Validation is shared so the API and the settings editor agree on what's allowed.
 */
/** Lines the trade chart can draw, in the order the settings list them. */
export const CHART_LINE_KEYS = ['long', 'short', 'tp', 'sl', 'pending', 'breakEven', 'bidAsk', 'last'];
export const CHART_LINE_NAMES = {
    long: 'Buy / Long entry',
    short: 'Sell / Short entry',
    tp: 'Take profit',
    sl: 'Stop loss',
    pending: 'Pending orders',
    breakEven: 'Break-even',
    bidAsk: 'Bid / Ask',
    last: 'Last price',
};
/**
 * A colour is a theme colour (`theme:up` …), which follows light and dark mode, a fixed `#rrggbb`,
 * or `auto` (the last-price line only: the colour of the last candle).
 */
export const CHART_THEME_COLORS = ['up', 'down', 'warn', 'accent', 'text', 'muted'];
export const CHART_TYPES = ['candles', 'hollow', 'heikinAshi', 'bars', 'line', 'area'];
const line = (color, style, show = true, width = 1) => ({ show, color, style, width, label: true });
export const DEFAULT_CHART_LOOK = {
    lines: {
        long: line('theme:up', 'solid'),
        short: line('theme:down', 'solid'),
        tp: line('theme:warn', 'dashed'),
        sl: line('theme:down', 'dashed'),
        pending: line('theme:accent', 'dashed'),
        breakEven: line('theme:muted', 'dashed', false),
        bidAsk: line('theme:muted', 'dotted', false),
        last: line('auto', 'dotted'),
    },
    type: 'candles',
    upColor: 'theme:up',
    downColor: 'theme:down',
    grid: true,
    volume: true,
    legend: true,
    pnl: 'usd',
    axisLabels: true,
    countdown: true,
    scale: 'normal',
    timezone: 'utc',
};
export const DEFAULT_CHART_PREFS = {
    look: DEFAULT_CHART_LOOK,
    overrides: {},
    templates: [null, null, null],
    drag: true,
    confirmDrag: true,
};
/** The look for one instrument: its override, or the default look. */
export const chartLookFor = (prefs, symbol) => prefs.overrides[symbol] ?? prefs.look;
export const MAX_CHART_OVERRIDES = 100;
export const MAX_TEMPLATE_NAME = 24;
const HEX_RE = /^#[0-9a-f]{6}$/;
const SYMBOL_RE = /^[A-Z0-9][A-Z0-9._-]{0,23}$/;
export const isChartColor = (x) => typeof x === 'string' && (x === 'auto' || HEX_RE.test(x) || (x.startsWith('theme:') && CHART_THEME_COLORS.includes(x.slice(6))));
function pick(x, allowed, fallback, label) {
    if (x === undefined)
        return fallback;
    if (typeof x !== 'string' || !allowed.includes(x))
        throw new Error(`${label} must be one of ${allowed.join(', ')}`);
    return x;
}
function bool(x, fallback, label) {
    if (x === undefined)
        return fallback;
    if (typeof x !== 'boolean')
        throw new Error(`${label} must be true or false`);
    return x;
}
function color(x, fallback, label) {
    if (x === undefined)
        return fallback;
    const v = typeof x === 'string' ? x.toLowerCase() : x;
    if (!isChartColor(v))
        throw new Error(`${label} must be a #rrggbb colour or a theme colour`);
    return v;
}
function obj(x, label) {
    if (x === undefined)
        return {};
    if (!x || typeof x !== 'object' || Array.isArray(x))
        throw new Error(`${label} must be an object`);
    return x;
}
function parseLine(x, d, label) {
    const o = obj(x, label);
    const width = o.width === undefined ? d.width : Number(o.width);
    if (width !== 1 && width !== 2 && width !== 3 && width !== 4)
        throw new Error(`${label} width must be 1 to 4`);
    return {
        show: bool(o.show, d.show, `${label} show`),
        color: color(o.color, d.color, `${label} colour`),
        style: pick(o.style, ['solid', 'dashed', 'dotted'], d.style, `${label} style`),
        width,
        label: bool(o.label, d.label, `${label} label`),
    };
}
/** Missing keys take their defaults, so looks saved by an older version keep loading. */
export function parseChartLook(x, label = 'look') {
    const o = obj(x, label);
    const d = DEFAULT_CHART_LOOK;
    const lines = obj(o.lines, `${label} lines`);
    return {
        lines: Object.fromEntries(CHART_LINE_KEYS.map((k) => [k, parseLine(lines[k], d.lines[k], CHART_LINE_NAMES[k])])),
        type: pick(o.type, CHART_TYPES, d.type, 'Chart type'),
        upColor: color(o.upColor, d.upColor, 'Rising candle colour'),
        downColor: color(o.downColor, d.downColor, 'Falling candle colour'),
        grid: bool(o.grid, d.grid, 'grid'),
        volume: bool(o.volume, d.volume, 'volume'),
        legend: bool(o.legend, d.legend, 'legend'),
        pnl: pick(o.pnl, ['usd', 'pct', 'off'], d.pnl, 'P&L on line'),
        axisLabels: bool(o.axisLabels, d.axisLabels, 'axisLabels'),
        countdown: bool(o.countdown, d.countdown, 'countdown'),
        scale: pick(o.scale, ['normal', 'log', 'percent'], d.scale, 'Scale'),
        timezone: pick(o.timezone, ['utc', 'local'], d.timezone, 'Timezone'),
    };
}
/** Validate and normalise prefs from a client. Throws Error with a readable message. */
export function parseChartPrefs(x) {
    if (!x || typeof x !== 'object' || Array.isArray(x))
        throw new Error('Preferences must be an object');
    const o = x;
    const rawOverrides = obj(o.overrides, 'overrides');
    const symbols = Object.keys(rawOverrides);
    if (symbols.length > MAX_CHART_OVERRIDES)
        throw new Error(`At most ${MAX_CHART_OVERRIDES} instruments can have their own chart settings`);
    const overrides = {};
    for (const s of symbols) {
        if (!SYMBOL_RE.test(s))
            throw new Error(`Unknown instrument ${s.slice(0, 24)}`);
        overrides[s] = parseChartLook(rawOverrides[s], s);
    }
    const rawTemplates = o.templates === undefined ? [null, null, null] : o.templates;
    if (!Array.isArray(rawTemplates) || rawTemplates.length !== 3)
        throw new Error('Exactly three template slots are required');
    const templates = rawTemplates.map((t, i) => {
        if (t === null)
            return null;
        const tt = obj(t, `Template ${i + 1}`);
        const name = typeof tt.name === 'string' ? tt.name.trim() : '';
        if (!name || name.length > MAX_TEMPLATE_NAME)
            throw new Error(`Template ${i + 1} needs a name of 1 to ${MAX_TEMPLATE_NAME} characters`);
        return { name, look: parseChartLook(tt.look, name) };
    });
    return {
        look: parseChartLook(o.look),
        overrides,
        templates,
        drag: bool(o.drag, DEFAULT_CHART_PREFS.drag, 'drag'),
        confirmDrag: bool(o.confirmDrag, DEFAULT_CHART_PREFS.confirmDrag, 'confirmDrag'),
    };
}
/** Heikin-Ashi candles from plain OHLC (numbers), oldest first. */
export function heikinAshi(bars) {
    const out = [];
    for (let i = 0; i < bars.length; i++) {
        const b = bars[i];
        const prev = out[i - 1];
        const c = (b.o + b.h + b.l + b.c) / 4;
        const o = prev ? (prev.o + prev.c) / 2 : (b.o + b.c) / 2;
        out.push({ ...b, o, c, h: Math.max(b.h, o, c), l: Math.min(b.l, o, c) });
    }
    return out;
}
/**
 * Price where every open position on one instrument together breaks even: unrealized P&L at that price
 * pays back the swap and opening fees already charged. Null when the net position is flat.
 * `legs` take `dir` +1 for long and -1 for short; `usdPerPoint` converts a 1-point move on 1 unit to USD.
 */
export function breakEvenPrice(legs, usdPerPoint) {
    let units = 0;
    let cost = 0;
    for (const l of legs) {
        units += l.dir * l.qty * usdPerPoint;
        // P&L(P) = Σ dir·(P − entry)·qty·upp + swap − fee  →  P·units − Σ dir·entry·qty·upp + Σ(swap − fee)
        cost += l.dir * l.entry * l.qty * usdPerPoint - l.swap + l.fee;
    }
    if (Math.abs(units) < 1e-12)
        return null;
    const p = cost / units;
    return Number.isFinite(p) && p > 0 ? p : null;
}
//# sourceMappingURL=chart-prefs.js.map